import {
    ChangeEvent,
    FocusEvent,
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { BaseTextField, BaseTextFieldProps } from './BaseTextField';

export interface NumberFieldProps
    extends Omit<BaseTextFieldProps, 'onChange' | 'value' | 'defaultValue' | 'type'> {
    value?: number | string;
    defaultValue?: number | string;
    min?: number;
    max?: number;
    step?: number;
    /** When true, allows decimal input (default: true if step < 1, false otherwise) */
    allowDecimals?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Validates if a string is a valid number input (allows intermediate states like "0." or "0.0")
 */
const isValidNumberInput = (value: string, allowDecimals: boolean, step: number): boolean => {
    if (value === '' || value === '-') return true;

    if (!allowDecimals) {
        return /^-?\d*$/.test(value);
    }

    // Calculate max decimal places from step
    const stepStr = step.toString();
    const decimalIndex = stepStr.indexOf('.');
    const maxDecimals = decimalIndex === -1 ? 2 : stepStr.length - decimalIndex - 1;

    // Allow intermediate states: ".", "0.", "0.0", etc.
    const pattern = new RegExp(`^-?\\d*\\.?\\d{0,${maxDecimals}}$`);
    return pattern.test(value);
};

/**
 * NumberField - A text input optimized for numeric values
 *
 * Uses type="text" with inputMode="decimal" instead of type="number" to:
 * - Allow intermediate states like "0." and "0.0" during typing
 * - Provide consistent behavior across all browsers
 * - Still show numeric keyboard on mobile devices
 */
export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
    (
        {
            value,
            defaultValue,
            min,
            max,
            step = 1,
            allowDecimals,
            onChange,
            onBlur,
            onFocus,
            ...props
        },
        ref,
    ) => {
        // Default allowDecimals based on step value
        const shouldAllowDecimals = allowDecimals ?? step < 1;

        // Track the display value separately to allow intermediate states
        const [displayValue, setDisplayValue] = useState<string>(() => {
            if (value !== undefined && value !== null) return String(value);
            if (defaultValue !== undefined && defaultValue !== null) return String(defaultValue);
            return '';
        });
        const isEditingRef = useRef(false);
        const inputRef = useRef<HTMLInputElement>(null);

        // Sync display value with controlled value when not editing
        useEffect(() => {
            if (!isEditingRef.current && value !== undefined) {
                setDisplayValue(value !== null ? String(value) : '');
            }
        }, [value]);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Allow navigation and editing keys
            const allowedKeys = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Tab',
                'Enter',
                'Home',
                'End',
            ];

            if (allowedKeys.includes(e.key)) {
                return;
            }

            // Allow keyboard shortcuts (Ctrl/Cmd + A, C, V, X, Z)
            if (e.ctrlKey || e.metaKey) {
                return;
            }

            const isNumeric = /^[0-9]$/.test(e.key);
            const isDecimal = e.key === '.';
            const isMinus = e.key === '-';

            // Block decimal if not allowed
            if (!shouldAllowDecimals && isDecimal) {
                e.preventDefault();
                return;
            }

            // Block non-numeric, non-decimal, non-minus keys
            if (!isNumeric && !isDecimal && !isMinus) {
                e.preventDefault();
                return;
            }

            const target = e.target as HTMLInputElement;
            const currentValue = target.value;
            const selectionStart = target.selectionStart ?? currentValue.length;
            const selectionEnd = target.selectionEnd ?? currentValue.length;

            // Build the prospective new value
            const beforeSelection = currentValue.substring(0, selectionStart);
            const afterSelection = currentValue.substring(selectionEnd);
            const prospectiveValue = beforeSelection + e.key + afterSelection;

            // Validate the prospective value
            if (!isValidNumberInput(prospectiveValue, shouldAllowDecimals, step)) {
                e.preventDefault();
            }
        };

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                isEditingRef.current = true;
                const newValue = event.target.value;

                // Allow empty string and valid number inputs
                if (newValue === '' || isValidNumberInput(newValue, shouldAllowDecimals, step)) {
                    setDisplayValue(newValue);
                    // Pass through to parent - they'll get the string value
                    onChange?.(event);
                }
            },
            [onChange, shouldAllowDecimals, step],
        );

        const handleBlur = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                isEditingRef.current = false;

                // Clean up the display value on blur (e.g., "0." -> "0")
                const currentValue = displayValue;
                if (currentValue !== '' && currentValue !== null) {
                    const numericValue = parseFloat(currentValue);
                    if (!Number.isNaN(numericValue)) {
                        // Clamp to min/max if provided
                        let clampedValue = numericValue;
                        if (min !== undefined && clampedValue < min) clampedValue = min;
                        if (max !== undefined && clampedValue > max) clampedValue = max;

                        // Format to proper decimal places
                        const formattedValue = shouldAllowDecimals
                            ? clampedValue.toString()
                            : Math.round(clampedValue).toString();
                        if (formattedValue !== currentValue) {
                            setDisplayValue(formattedValue);
                        }
                    }
                }

                onBlur?.(event);
            },
            [displayValue, shouldAllowDecimals, min, max, onBlur],
        );

        const handleFocus = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                isEditingRef.current = true;
                onFocus?.(event);
            },
            [onFocus],
        );

        // Combine refs
        const combinedRef = useCallback(
            (node: HTMLInputElement | null) => {
                inputRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            },
            [ref],
        );

        return (
            <BaseTextField
                {...props}
                ref={combinedRef}
                type="text"
                inputMode={shouldAllowDecimals ? 'decimal' : 'numeric'}
                value={displayValue}
                min={min}
                max={max}
                step={step}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={handleFocus}
            />
        );
    },
);

NumberField.displayName = 'NumberField';

export default NumberField;
